import { useEffect, useMemo, useState } from 'react';
import { Loader2, MapPin, Plus, Check, Users, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { haversineKm, AGENCE_CYGNATURE, type Coords } from '../utils/geo';

interface Props {
  /** Coordonnées du lieu de l'événement (si null → fallback agence Troyes) */
  lieuCoords: Coords | null;
  /** Texte du lieu (juste pour info / titre) */
  lieuLabel?: string;
  /** Domaines souhaités par le client (filtre prioritaire) */
  domaines: string[];
  /** Artistes déjà sélectionnés (nom_scene) */
  selected: string[];
  /** Ajouter / retirer un artiste */
  onToggle: (nomScene: string) => void;
  /** Limite d'affichage */
  limit?: number;
}

interface ArtisteRow {
  id: string;
  nom_scene: string;
  ville: string | null;
  domaine_principal: string | null;
  domaines: string[] | null;
  lat: number | null;
  lng: number | null;
  tarif_base: number | null;
  photo_url: string | null;
}

interface Ranked extends ArtisteRow {
  distanceKm: number | null;
  matchDomaine: boolean;
}

/**
 * Suggère les artistes les plus proches du lieu de l'événement,
 * filtrés/priorisés par domaines artistiques souhaités par le client.
 * Utilise les coordonnées (lat/lng) déjà géocodées en base.
 */
export default function ArtistesProches({
  lieuCoords,
  lieuLabel,
  domaines,
  selected,
  onToggle,
  limit = 10,
}: Props) {
  const [artistes, setArtistes] = useState<ArtisteRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    supabase
      .from('artistes')
      .select('id, nom_scene, ville, domaine_principal, domaines, lat, lng, tarif_base, photo_url')
      .is('deleted_at', null)
      .is('archived_at', null)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error('[ArtistesProches]', error);
          setArtistes([]);
        } else {
          setArtistes((data || []) as ArtisteRow[]);
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const origin: Coords = lieuCoords || AGENCE_CYGNATURE;
  const fallbackToAgency = !lieuCoords;

  const ranked = useMemo<Ranked[]>(() => {
    const domainesLower = domaines.map((d) => d.toLowerCase().trim()).filter(Boolean);

    const enriched = artistes.map<Ranked>((a) => {
      const arrDom = (a.domaines || []).map((d) => d.toLowerCase().trim());
      const principal = (a.domaine_principal || '').toLowerCase().trim();
      const matchDomaine =
        domainesLower.length === 0
          ? true
          : domainesLower.some((d) => arrDom.includes(d) || principal === d);

      const distanceKm =
        a.lat != null && a.lng != null
          ? haversineKm(origin, { lat: Number(a.lat), lon: Number(a.lng) })
          : null;

      return { ...a, matchDomaine, distanceKm };
    });

    // Si des domaines sont demandés, on garde uniquement ceux qui matchent
    const filtered = domainesLower.length > 0 ? enriched.filter((a) => a.matchDomaine) : enriched;

    // Tri : distance croissante (les non-géocodés en dernier)
    filtered.sort((a, b) => {
      if (a.distanceKm == null && b.distanceKm == null) return 0;
      if (a.distanceKm == null) return 1;
      if (b.distanceKm == null) return -1;
      return a.distanceKm - b.distanceKm;
    });

    return filtered.slice(0, limit);
  }, [artistes, domaines, origin, limit]);

  return (
    <div className="border border-slate-800 rounded-lg bg-slate-950/40 p-3 space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
          <Users size={14} strokeWidth={1.5} className="text-amber-400" />
          Artistes les plus proches
          {domaines.length > 0 && (
            <span className="text-[10px] text-amber-400/80 font-normal">
              · filtrés sur {domaines.length} domaine{domaines.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="text-[10px] text-slate-500 flex items-center gap-1">
          <MapPin size={10} strokeWidth={1.5} />
          Depuis : {fallbackToAgency ? 'Agence (Saint-Pouange)' : lieuLabel || 'lieu sélectionné'}
        </div>
      </div>

      {fallbackToAgency && (
        <div className="text-[10px] text-amber-400/80 italic flex items-center gap-1">
          <AlertCircle size={10} strokeWidth={1.5} />
          Sélectionnez le lieu de l'événement pour un classement précis.
        </div>
      )}

      {loading ? (
        <div className="text-xs text-slate-500 italic flex items-center gap-2">
          <Loader2 size={12} className="animate-spin text-amber-400" /> Chargement…
        </div>
      ) : ranked.length === 0 ? (
        <div className="text-xs text-slate-500 italic">
          Aucun artiste correspondant{domaines.length > 0 ? ' à ces domaines' : ''}.
        </div>
      ) : (
        <ul className="space-y-1 max-h-72 overflow-y-auto pr-1">
          {ranked.map((a) => {
            const isSel = selected.includes(a.nom_scene);
            return (
              <li
                key={a.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-slate-900/50 border border-slate-800/60 hover:border-amber-500/30"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-200 truncate">{a.nom_scene}</div>
                  <div className="text-[10px] text-slate-500 truncate flex items-center gap-1.5">
                    {a.domaine_principal && <span>{a.domaine_principal}</span>}
                    {a.ville && <span>· {a.ville}</span>}
                    {a.distanceKm != null ? (
                      <span className="text-amber-400/90">· ~{Math.round(a.distanceKm)} km</span>
                    ) : (
                      <span className="text-slate-600 italic">· non géocodé</span>
                    )}
                    {a.tarif_base != null && <span>· {a.tarif_base}€</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onToggle(a.nom_scene)}
                  className={`shrink-0 px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1 transition-colors ${
                    isSel
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                      : 'bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25'
                  }`}
                  title={isSel ? 'Retirer' : 'Ajouter à la prestation'}
                >
                  {isSel ? (
                    <>
                      <Check size={10} strokeWidth={2} /> Ajouté
                    </>
                  ) : (
                    <>
                      <Plus size={10} strokeWidth={2} /> Ajouter
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
